/**
 * One way to run a Squares mutation: a refusal raises the shared error modal
 * through `showApiError`, a rejected request has already raised the offline
 * modal from the app's MutationCache, and the caller gets the data or null.
 * Every refusal resolves through the `squares` context, so the per player
 * limit reads as a limit and a taken square says what to do next.
 */
import { usePoolGet } from "@sportsmet/pool-sdk/private";
import { confirmDestructive } from "@sportsmet/ui";

import { showApiError } from "../../errors/apiErrorBus";

export async function runSquaresMutation<TResult>(
  call: () => Promise<TResult>,
  onSuccess?: () => void,
): Promise<TResult | null> {
  let result: TResult;
  try {
    result = await call();
  } catch {
    return null;
  }
  // The squares context names the real reason for a limit, a frozen setting,
  // an unfinished season or a taken square instead of the generic conflict
  // sentence.
  if (showApiError(result, "squares")) return null;
  onSuccess?.();
  return result;
}

/**
 * ISSUE-1280 QA. The pool header's line ("Grid open · 3 of 5 squares",
 * "Grid locked") comes from pool-api's /pool/get, which no Squares mutation
 * invalidates on its own. A Squares change that moves that line refetches it,
 * so the header never reads "Grid open" over a grid the admin just locked.
 */
export function useRefreshSquaresPoolLine(poolId: string): () => void {
  const pool = usePoolGet({ poolId });
  return () => {
    void pool.refetch();
  };
}

/**
 * The unsaved-changes guard for a Squares sheet: Escape, the scrim and the X
 * ask first, and only an explicit Discard drops what was picked or typed.
 */
export async function confirmSquaresDiscard(thing: string): Promise<boolean> {
  return confirmDestructive({
    title: `Discard this ${thing}?`,
    body: "What you picked or typed is not saved.",
    confirmLabel: `Discard ${thing}`,
    cancelLabel: "Keep editing",
  });
}
